import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Container,
  Grid,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  ListItemText as MuiListItemText,
  Fade,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  CardMedia
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Psychology as PsychologyIcon,
  TextFields as TextFieldsIcon,
  Undo as UndoIcon,
  InfoOutlined as InfoIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  LibraryBooks as PassagesIcon,
  PlaylistPlay as SequencesIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { AuthContext } from '../App';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import LineVideoManager from '../components/LineVideoManager';
import HeroVideoManager from '../components/HeroVideoManager';

// Settings categories configuration (keeping existing settings functionality)
const settingsConfig = {
  ai_analysis: {
    title: 'AI Analysis',
    icon: <PsychologyIcon />,
    color: '#4fc3f7',
    description: 'Configure AI-powered reading analysis',
    settings: [
      {
        key: 'gpt_analysis_instruction',
        label: 'GPT Analysis Instruction Prompt',
        description: 'Customize the instructions given to GPT for analyzing student reading performance',
        type: 'textarea',
        rows: 20,
        required: true
      }
    ]
  },
  general: {
    title: 'General',
    icon: <SettingsIcon />,
    color: '#81c784',
    description: 'Basic application settings',
    settings: [
      {
        key: 'app_name',
        label: 'Application Name',
        description: 'The name displayed in the application header',
        type: 'text',
        required: true
      }
    ]
  },
  grading: {
    title: 'Grading',
    icon: <TextFieldsIcon />,
    color: '#ffb74d',
    description: 'Grading method preferences',
    settings: [
      {
        key: 'default_grading_method',
        label: 'Default Grading Method',
        description: 'Default method for grading new tests',
        type: 'select',
        options: [
          { value: 'whisper', label: 'Whisper AI' },
          { value: 'speechace', label: 'SpeechAce' }
        ],
        required: true
      }
    ]
  }
};

// Admin tabs configuration
const adminTabs = [
  {
    id: 'settings',
    label: 'System Settings',
    icon: <SettingsIcon />,
    description: 'Configure application settings'
  },
  {
    id: 'teachers',
    label: 'Teacher Management',
    icon: <PeopleIcon />,
    description: 'Manage teacher accounts'
  },
  {
    id: 'passages',
    label: 'All Passages',
    icon: <PassagesIcon />,
    description: 'View all passages across teachers'
  },
  {
    id: 'sequences',
    label: 'All Sequences',
    icon: <SequencesIcon />,
    description: 'View all sequences across teachers'
  },
  {
    id: 'analytics',
    label: 'Analytics & Reports',
    icon: <AnalyticsIcon />,
    description: 'View system analytics and reports'
  }
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function Admin() {
  const { teacher } = useContext(AuthContext);
  const navigate = useNavigate();

  // All hooks must be called before any conditional returns
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Settings state (for settings tab)
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('ai_analysis');
  const [unsavedChanges, setUnsavedChanges] = useState({});

  // Teachers state
  const [teachers, setTeachers] = useState([]);
  const [teacherDialog, setTeacherDialog] = useState({ open: false, mode: 'create', teacher: null });
  const [teacherForm, setTeacherForm] = useState({ name: '', email: '', password: '', role: 'teacher' });

  // Data states
  const [passages, setPassages] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [analytics, setAnalytics] = useState({});

  // Create passage states (matching Dashboard exactly)
  const [createPassageOpen, setCreatePassageOpen] = useState(false);
  const [newPassageName, setNewPassageName] = useState('');
  const [newPassageText, setNewPassageText] = useState('');
  const [newPassageImage, setNewPassageImage] = useState(null);
  const [newPassageImagePreview, setNewPassageImagePreview] = useState(null);
  const [uploadingPassage, setUploadingPassage] = useState(false);
  const [newPassageType, setNewPassageType] = useState('assessment');
  const [newPassageGradeLevel, setNewPassageGradeLevel] = useState('');
  const [newPassageStoryType, setNewPassageStoryType] = useState('');
  const [newPassageSource, setNewPassageSource] = useState('');

  // Edit passage states
  const [isEditingPassage, setIsEditingPassage] = useState(false);
  const [editingPassageId, setEditingPassageId] = useState(null);
  const [lineVideos, setLineVideos] = useState([]);
  const [heroVideo, setHeroVideo] = useState(null);

  // Create sequence states (matching Dashboard exactly)
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [selectedPassages, setSelectedPassages] = useState([]);
  const [sequenceStep, setSequenceStep] = useState(0);
  const [saveSequenceMode, setSaveSequenceMode] = useState(false);
  const [sequenceTitle, setSequenceTitle] = useState('');
  const [sequenceDescription, setSequenceDescription] = useState('');
  const [savingSequence, setSavingSequence] = useState(false);

  // Edit sequence states
  const [isEditingSequence, setIsEditingSequence] = useState(false);
  const [editingSequenceId, setEditingSequenceId] = useState(null);

  // Passages filter states
  const [passageSearchQuery, setPassageSearchQuery] = useState('');
  const [filterGradeLevels, setFilterGradeLevels] = useState([]);
  const [filterPassageType, setFilterPassageType] = useState('');
  const [filterStoryTypes, setFilterStoryTypes] = useState([]);
  const [filterSources, setFilterSources] = useState([]);
  const [filterLexileBucket, setFilterLexileBucket] = useState('any');
  const [filterPassageTeacher, setFilterPassageTeacher] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Sequences search and filter states
  const [sequenceSearchQuery, setSequenceSearchQuery] = useState('');
  const [filterSequenceTeacher, setFilterSequenceTeacher] = useState('');

  // Results dialogs state
  const [passageResultsOpen, setPassageResultsOpen] = useState(false);
  const [passageResultsLoading, setPassageResultsLoading] = useState(false);
  const [passageResults, setPassageResults] = useState([]);
  const [passageResultsTitle, setPassageResultsTitle] = useState('');

  const [sequenceResultsOpen, setSequenceResultsOpen] = useState(false);
  const [sequenceResultsLoading, setSequenceResultsLoading] = useState(false);
  const [sequenceRuns, setSequenceRuns] = useState([]);
  const [sequenceResultsTitle, setSequenceResultsTitle] = useState('');

  // All functions must be defined before any conditional returns
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Load data for the selected tab
    switch (newValue) {
      case 0: // Settings
        fetchSettings();
        break;
      case 1: // Teachers
        fetchTeachers();
        break;
      case 2: // Passages - needs teachers for filtering
        fetchPassages();
        fetchTeachers(); // Always fetch teachers for filtering
        break;
      case 3: // Sequences - needs teachers for filtering
        fetchSequences();
        fetchTeachers(); // Always fetch teachers for filtering
        break;
      case 4: // Analytics
        fetchAnalytics();
        break;
      default:
        break;
    }
  };

  // Settings functions
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/settings');
      setSettings(response.data);
      setUnsavedChanges({});
    } catch (error) {
      console.error('Error fetching settings:', error);
      showSnackbar('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Teacher management functions
  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/teachers');
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      showSnackbar('Failed to load teachers', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Data fetching functions
  const fetchPassages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/passages');
      setPassages(response.data);
    } catch (error) {
      console.error('Error fetching passages:', error);
      showSnackbar('Failed to load passages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSequences = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/sequences');
      setSequences(response.data);
    } catch (error) {
      console.error('Error fetching sequences:', error);
      showSnackbar('Failed to load sequences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      showSnackbar('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Admin results handlers
  const handleViewPassageResults = async (passage) => {
    try {
      setPassageResultsTitle(passage.title || 'Passage Results');
      setPassageResultsOpen(true);
      setPassageResultsLoading(true);
      const res = await api.get(`/api/admin/passages/${passage._id}/tests`);
      setPassageResults(res.data || []);
    } catch (error) {
      console.error('Error fetching passage results:', error);
      showSnackbar(error.response?.data?.error || 'Failed to fetch passage results', 'error');
    } finally {
      setPassageResultsLoading(false);
    }
  };

  const handleViewSequenceResults = async (sequence) => {
    try {
      setSequenceResultsTitle(sequence.title || 'Sequence Results');
      setSequenceResultsOpen(true);
      setSequenceResultsLoading(true);
      const res = await api.get(`/api/admin/saved-sequences/${sequence._id}/runs`);
      setSequenceRuns(res.data || []);
    } catch (error) {
      console.error('Error fetching sequence runs:', error);
      showSnackbar(error.response?.data?.error || 'Failed to fetch sequence runs', 'error');
    } finally {
      setSequenceResultsLoading(false);
    }
  };

  // Utility functions (from teacher dashboard)
  const calculateWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Image handling functions (from teacher dashboard)
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPassageImage(file);
      setNewPassageImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setNewPassageImage(file);
      setNewPassageImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageRemove = () => {
    setNewPassageImage(null);
    setNewPassageImagePreview(null);
  };

  // Utility helper functions
  const getExcerpt = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Open passage creation dialog
  const handleOpenCreatePassage = () => {
    setIsEditingPassage(false);
    setEditingPassageId(null);
    setNewPassageName('');
    setNewPassageText('');
    setNewPassageType('assessment');
    setNewPassageGradeLevel('');
    setNewPassageStoryType('');
    setNewPassageSource('');
    setNewPassageImage(null);
    setNewPassageImagePreview(null);
    setLineVideos([]);
    setHeroVideo(null);
    setCreatePassageOpen(true);
  };

  // Open passage edit dialog
  const handleEditPassage = (passage) => {
    setIsEditingPassage(true);
    setEditingPassageId(passage._id);
    setNewPassageName(passage.title);
    setNewPassageText(passage.text);
    setNewPassageType(passage.passageType || 'assessment');
    setNewPassageGradeLevel(passage.gradeLevel || '');
    setNewPassageStoryType(passage.storyType || '');
    setNewPassageSource(passage.source || '');
    setNewPassageImage(null);
    setLineVideos(passage.lineVideos || []);
    setHeroVideo(passage.heroVideo || null);
    if (passage.image) {
      const imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/passages/${passage.image}`;
      setNewPassageImagePreview(imageUrl);
    } else {
      setNewPassageImagePreview(null);
    }
    setCreatePassageOpen(true);
  };

  // Delete passage
  const handleDeletePassage = async (passage) => {
    if (window.confirm(`Are you sure you want to delete "${passage.title}"? This action cannot be undone.`)) {
      try {
        await api.delete(`/api/passages/${passage._id}`);
        showSnackbar('Passage deleted successfully', 'success');
        fetchPassages(); // Refresh the passages list
      } catch (error) {
        console.error('Error deleting passage:', error);
        showSnackbar(error.response?.data?.error || 'Failed to delete passage', 'error');
      }
    }
  };

  // Create/Edit passage submit
  const handleCreatePassage = async () => {
    if (!newPassageName.trim() || !newPassageText.trim() || !newPassageGradeLevel || !newPassageSource) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    if (newPassageType === 'story' && !newPassageStoryType) {
      showSnackbar('Story type is required for story passages', 'error');
      return;
    }
    setUploadingPassage(true);
    try {
      const isEditing = isEditingPassage && editingPassageId;
      let res;

      if (newPassageImage) {
        // Upload image and passage in one request (multipart/form-data)
        const formData = new FormData();
        formData.append('title', newPassageName);
        formData.append('text', newPassageText);
        formData.append('passageType', newPassageType);
        formData.append('gradeLevel', newPassageGradeLevel);
        formData.append('source', newPassageSource);
        if (newPassageType === 'story') {
          formData.append('storyType', newPassageStoryType);
        }
        formData.append('image', newPassageImage);

        res = isEditing
          ? await api.put(`/api/passages/${editingPassageId}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
          : await api.post('/api/passages', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });

        if (!isEditing) {
          setPassages([{ ...res.data, createdAt: new Date().toISOString() }, ...passages]);
        }
      } else {
        // No image, just send data
        const passageData = {
          title: newPassageName,
          text: newPassageText,
          passageType: newPassageType,
          gradeLevel: newPassageGradeLevel,
          source: newPassageSource
        };
        if (newPassageType === 'story') {
          passageData.storyType = newPassageStoryType;
        }

        res = isEditing
          ? await api.put(`/api/passages/${editingPassageId}`, passageData)
          : await api.post('/api/passages', passageData);

        if (!isEditing) {
          setPassages([{ ...res.data, createdAt: new Date().toISOString() }, ...passages]);
        }
      }

      if (isEditing) {
        // When editing, close dialog and reset
        showSnackbar('Passage updated successfully!', 'success');
        setCreatePassageOpen(false);

        // Reset form
        setIsEditingPassage(false);
        setEditingPassageId(null);
        setNewPassageName('');
        setNewPassageText('');
        setNewPassageType('assessment');
        setNewPassageGradeLevel('');
        setNewPassageStoryType('');
        setNewPassageSource('');
        setNewPassageImage(null);
        setNewPassageImagePreview(null);
        setLineVideos([]);
      } else {
        // When creating, switch to edit mode so user can immediately add videos
        showSnackbar('Passage created! You can now add video instructions.', 'success');

        // Switch to edit mode with the newly created passage
        setIsEditingPassage(true);
        setEditingPassageId(res.data._id);
        // Clear the image since it's already uploaded
        setNewPassageImage(null);
        setNewPassageImagePreview(res.data.image ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/passages/${res.data.image}` : null);
      }

      fetchPassages(); // Refresh the passages list
    } catch (error) {
      const action = isEditingPassage ? 'update' : 'create';
      showSnackbar(error.response?.data?.error || `Failed to ${action} passage`, 'error');
    } finally {
      setUploadingPassage(false);
    }
  };

  // Create sequence handlers (matching Dashboard exactly)
  const getPassageImage = (passage) => {
    return passage.image 
      ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/passages/${passage.image}`
      : '/Sea-Creatures-and-Sea-Life.png';
  };

  const getPassageSequenceNumber = (passageId) => {
    const index = selectedPassages.findIndex(p => p._id === passageId);
    return index >= 0 ? index + 1 : null;
  };

  const handlePassageToggle = (passage) => {
    const existingIndex = selectedPassages.findIndex(p => p._id === passage._id);
    if (existingIndex >= 0) {
      // Remove passage
      setSelectedPassages(selectedPassages.filter((_, index) => index !== existingIndex));
    } else {
      // Add passage
      setSelectedPassages([...selectedPassages, passage]);
    }
  };

  const handleSequenceNext = () => {
    if (selectedPassages.length === 0) {
      showSnackbar('Please select at least one passage', 'error');
      return;
    }
    setSequenceStep(1);
    setSaveSequenceMode(true); // Start in save mode for admin
  };

  const handleSequenceBack = () => {
    setSequenceStep(0);
  };

  const handleSaveSequence = async () => {
    if (!sequenceTitle.trim() || selectedPassages.length === 0) return;
    
    setSavingSequence(true);
    try {
      // Validate passages before sending
      const validPassages = selectedPassages.filter(p => p && p._id);
      if (validPassages.length === 0) {
        throw new Error('No valid passages selected');
      }
      
      const payload = {
        title: sequenceTitle,
        description: sequenceDescription,
        passages: validPassages.map((p, index) => ({ 
          passageId: p._id, 
          sequenceOrder: index + 1 
        }))
      };
      
      console.log('Saving sequence with payload:', payload);
      
      const isEditing = isEditingSequence && editingSequenceId;
      
      if (isEditing) {
        await api.put(`/api/saved-sequences/${editingSequenceId}`, payload);
        showSnackbar('Sequence updated successfully!', 'success');
      } else {
        await api.post('/api/saved-sequences', payload);
        showSnackbar('Sequence saved successfully!', 'success');
      }
      
      setSequenceDialogOpen(false);
      
      // Reset form
      setIsEditingSequence(false);
      setEditingSequenceId(null);
      setSequenceTitle('');
      setSequenceDescription('');
      setSaveSequenceMode(false);
      setSequenceStep(0);
      setSelectedPassages([]);
      
      fetchSequences(); // Refresh the sequences list
    } catch (error) {
      console.error('Error saving sequence:', error);
      const action = isEditingSequence ? 'update' : 'save';
      showSnackbar(error.response?.data?.error || `Failed to ${action} sequence`, 'error');
    } finally {
      setSavingSequence(false);
    }
  };

  const handleAdminCreateSequence = () => {
    setIsEditingSequence(false);
    setEditingSequenceId(null);
    setSelectedPassages([]);
    setSequenceStep(0);
    setSaveSequenceMode(true); // Admin always saves sequences, doesn't assign to students
    setSequenceTitle('');
    setSequenceDescription('');
    setSequenceDialogOpen(true);
  };

  // Edit sequence
  const handleEditSequence = async (sequence) => {
    try {
      // Fetch the full sequence details with passages
      const response = await api.get(`/api/saved-sequences/${sequence._id}`);
      const fullSequence = response.data;
      
      setIsEditingSequence(true);
      setEditingSequenceId(sequence._id);
      setSequenceTitle(fullSequence.title);
      setSequenceDescription(fullSequence.description || '');
      
      // Pre-select the passages in order
      const orderedPassages = fullSequence.passages
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        .map(p => p.passageId);
      setSelectedPassages(orderedPassages);
      
      setSequenceStep(1); // Go directly to the save step for editing
      setSaveSequenceMode(true);
      setSequenceDialogOpen(true);
    } catch (error) {
      console.error('Error fetching sequence details:', error);
      showSnackbar('Failed to load sequence details', 'error');
    }
  };

  // Delete sequence
  const handleDeleteSequence = async (sequence) => {
    if (window.confirm(`Are you sure you want to delete "${sequence.title}"? This action cannot be undone.`)) {
      try {
        await api.delete(`/api/saved-sequences/${sequence._id}`);
        showSnackbar('Sequence deleted successfully', 'success');
        fetchSequences(); // Refresh the sequences list
      } catch (error) {
        console.error('Error deleting sequence:', error);
        showSnackbar(error.response?.data?.error || 'Failed to delete sequence', 'error');
      }
    }
  };

  // Filter helper functions for passages
  const resetPassageFilters = () => {
    setPassageSearchQuery('');
    setFilterGradeLevels([]);
    setFilterPassageType('');
    setFilterStoryTypes([]);
    setFilterSources([]);
    setFilterLexileBucket('any');
    setFilterPassageTeacher('');
  };

  const lexileInBucket = (lexileLevel, bucket) => {
    if (!bucket || bucket === 'any') return true;
    if (bucket === 'unknown') return !lexileLevel || Number.isNaN(parseInt(lexileLevel, 10));
    const n = parseInt(lexileLevel, 10);
    if (Number.isNaN(n)) return false;
    if (bucket === '0-199') return n >= 0 && n <= 199;
    if (bucket === '200-499') return n >= 200 && n <= 499;
    if (bucket === '500-799') return n >= 500 && n <= 799;
    if (bucket === '800+') return n >= 800;
    return true;
  };

  // Get filter chips for display
  const getFilterChips = () => {
    const chips = [];
    if (passageSearchQuery.trim()) {
      chips.push({ 
        key: 'search', 
        label: `"${passageSearchQuery}"`, 
        onDelete: () => setPassageSearchQuery('') 
      });
    }
    if (filterGradeLevels.length > 0) {
      chips.push({ 
        key: 'grade', 
        label: `Grade: ${filterGradeLevels.join(', ')}`, 
        onDelete: () => setFilterGradeLevels([]) 
      });
    }
    if (filterPassageType) {
      chips.push({ 
        key: 'type', 
        label: `Type: ${filterPassageType}`, 
        onDelete: () => setFilterPassageType('') 
      });
    }
    if (filterStoryTypes.length > 0) {
      chips.push({ 
        key: 'story', 
        label: `Genre: ${filterStoryTypes.join(', ')}`, 
        onDelete: () => setFilterStoryTypes([]) 
      });
    }
    if (filterSources.length > 0) {
      chips.push({ 
        key: 'source', 
        label: `Source: ${filterSources.join(', ')}`, 
        onDelete: () => setFilterSources([]) 
      });
    }
    if (filterLexileBucket !== 'any') {
      chips.push({ 
        key: 'lexile', 
        label: `Lexile: ${filterLexileBucket === 'unknown' ? 'Unknown' : filterLexileBucket}`, 
        onDelete: () => setFilterLexileBucket('any') 
      });
    }
    if (filterPassageTeacher) {
      const teacher = teachers.find(t => t._id === filterPassageTeacher);
      chips.push({ 
        key: 'teacher', 
        label: `Teacher: ${teacher?.name || 'Unknown'}`, 
        onDelete: () => setFilterPassageTeacher('') 
      });
    }
    return chips;
  };

  // Get active filter count for passages - must be before conditional return
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (passageSearchQuery.trim()) count++;
    if (filterGradeLevels.length > 0) count++;
    if (filterPassageType) count++;
    if (filterStoryTypes.length > 0) count++;
    if (filterSources.length > 0) count++;
    if (filterLexileBucket !== 'any') count++;
    if (filterPassageTeacher) count++;
    return count;
  }, [passageSearchQuery, filterGradeLevels, filterPassageType, filterStoryTypes, filterSources, filterLexileBucket, filterPassageTeacher]);

  // Filter passages - must be before conditional return
  const filteredPassages = useMemo(() => {
    const q = passageSearchQuery.trim().toLowerCase();
    return passages.filter(p => {
      if (q && !(p.title?.toLowerCase().includes(q) || p.text?.toLowerCase().includes(q))) return false;
      if (filterGradeLevels.length > 0 && !filterGradeLevels.includes(p.gradeLevel)) return false;
      if (filterPassageType && p.passageType !== filterPassageType) return false;
      if (filterStoryTypes.length > 0) {
        if (p.passageType !== 'story') return false;
        if (!p.storyType || !filterStoryTypes.includes(p.storyType)) return false;
      }
      if (filterSources.length > 0 && !filterSources.includes(p.source)) return false;
      if (!lexileInBucket(p.lexileLevel, filterLexileBucket)) return false;
      if (filterPassageTeacher && p.createdBy?._id !== filterPassageTeacher) return false;
      return true;
    });
  }, [passages, passageSearchQuery, filterGradeLevels, filterPassageType, filterStoryTypes, filterSources, filterLexileBucket, filterPassageTeacher]);

  // Filter sequences - must be before conditional return
  const filteredSequences = useMemo(() => {
    const q = sequenceSearchQuery.trim().toLowerCase();
    return sequences.filter(s => {
      // Text search filter
      if (q && !(s.title?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q))) return false;
      // Teacher filter
      if (filterSequenceTeacher && s.teacherId?._id !== filterSequenceTeacher) return false;
      return true;
    });
  }, [sequences, sequenceSearchQuery, filterSequenceTeacher]);

  // useEffect must also be called before any conditional returns
  useEffect(() => {
    // Load initial data based on active tab only if user is admin
    if (teacher && teacher.role === 'admin') {
      handleTabChange(null, activeTab);
    }
  }, [teacher]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug logging
  console.log('Admin component - Teacher object:', teacher);
  console.log('Admin component - Teacher role:', teacher?.role);
  console.log('Admin component - Is admin?', teacher?.role === 'admin');

  // Check if user is admin (after all hooks are declared)
  if (!teacher || teacher.role !== 'admin') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64, mx: 'auto', mb: 2 }}>
            <AdminIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700} mb={1}>Access Denied</Typography>
          <Typography color="text.secondary" mb={3}>
            You need administrator privileges to access this section.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/dashboard')}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #e3f2fd 0%, #fff3e0 100%)',
      fontFamily: 'Nunito, sans-serif'
    }}>
      {/* Header */}
      <Box sx={{ 
        background: '#fff', 
        borderBottom: '1px solid #e0e0e0',
        px: 4,
        py: 3
      }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton 
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  backgroundColor: '#f5f5f5', 
                  '&:hover': { backgroundColor: '#e0e0e0' } 
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Avatar sx={{ bgcolor: '#ff7043', width: 48, height: 48 }}>
                <AdminIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700} color="#333">
                  Admin Panel
                </Typography>
                <Typography color="text.secondary" fontSize={16}>
                  System administration and management
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => handleTabChange(null, activeTab)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  minHeight: 72,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '15px'
                }
              }}
            >
              {adminTabs.map((tab, index) => (
                <Tab 
                  key={tab.id}
                  icon={tab.icon}
                  iconPosition="start"
                  label={
                    <Box textAlign="left">
                      <Typography variant="inherit" fontWeight={600}>
                        {tab.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tab.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ minWidth: 200 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          {/* Settings Panel */}
          <TabPanel value={activeTab} index={0}>
            <SettingsPanel 
              settings={settings}
              setSettings={setSettings}
              settingsConfig={settingsConfig}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              unsavedChanges={unsavedChanges}
              setUnsavedChanges={setUnsavedChanges}
              saving={saving}
              setSaving={setSaving}
              showSnackbar={showSnackbar}
              teacher={teacher}
              loading={loading}
            />
          </TabPanel>

          {/* Teachers Panel */}
          <TabPanel value={activeTab} index={1}>
            <TeachersPanel 
              teachers={teachers}
              loading={loading}
              showSnackbar={showSnackbar}
              fetchTeachers={fetchTeachers}
            />
          </TabPanel>

          {/* Passages Panel */}
          <TabPanel value={activeTab} index={2}>
            <PassagesPanel 
              passages={filteredPassages}
              allPassages={passages}
              loading={loading}
              teachers={teachers}
              // Filter states
              passageSearchQuery={passageSearchQuery}
              setPassageSearchQuery={setPassageSearchQuery}
              filterGradeLevels={filterGradeLevels}
              setFilterGradeLevels={setFilterGradeLevels}
              filterPassageType={filterPassageType}
              setFilterPassageType={setFilterPassageType}
              filterStoryTypes={filterStoryTypes}
              setFilterStoryTypes={setFilterStoryTypes}
              filterSources={filterSources}
              setFilterSources={setFilterSources}
              filterLexileBucket={filterLexileBucket}
              setFilterLexileBucket={setFilterLexileBucket}
              filterPassageTeacher={filterPassageTeacher}
              setFilterPassageTeacher={setFilterPassageTeacher}
              filtersExpanded={filtersExpanded}
              setFiltersExpanded={setFiltersExpanded}
              // Filter helpers
              activeFilterCount={activeFilterCount}
              getFilterChips={getFilterChips}
              resetPassageFilters={resetPassageFilters}
              // Create passage handlers
              onCreatePassage={handleOpenCreatePassage}
              // Edit/delete passage handlers
              onEditPassage={handleEditPassage}
              onDeletePassage={handleDeletePassage}
              onViewPassageResults={handleViewPassageResults}
            />
          </TabPanel>

          {/* Sequences Panel */}
          <TabPanel value={activeTab} index={3}>
            <SequencesPanel 
              sequences={filteredSequences}
              allSequences={sequences}
              loading={loading}
              teachers={teachers}
              sequenceSearchQuery={sequenceSearchQuery}
              setSequenceSearchQuery={setSequenceSearchQuery}
              filterSequenceTeacher={filterSequenceTeacher}
              setFilterSequenceTeacher={setFilterSequenceTeacher}
              onCreateSequence={handleAdminCreateSequence}
              passages={passages}
              // Edit/delete sequence handlers
              onEditSequence={handleEditSequence}
              onDeleteSequence={handleDeleteSequence}
              onViewSequenceResults={handleViewSequenceResults}
            />
          </TabPanel>

          {/* Analytics Panel */}
          <TabPanel value={activeTab} index={4}>
            <AnalyticsPanel 
              analytics={analytics}
              loading={loading}
            />
          </TabPanel>
        </Paper>
      </Container>

      {/* Passage Results Dialog */}
      <Dialog open={passageResultsOpen} onClose={() => setPassageResultsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Results for: {passageResultsTitle}
        </DialogTitle>
        <DialogContent>
          {passageResultsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : passageResults.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No tests found for this passage.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {passageResults.map(test => (
                    <TableRow key={test._id} hover>
                      <TableCell>{test.childName || '—'}</TableCell>
                      <TableCell>{test.teacherId?.name || 'Unknown'}</TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={test.gradingMethod === 'whisper' ? 'Whisper' : 'SpeechAce'} 
                          color={test.gradingMethod === 'whisper' ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{test.status}</TableCell>
                      <TableCell>{new Date(test.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button 
                            size="small" 
                            variant="contained" 
                            disabled={test.status !== 'attempted'}
                            onClick={() => window.open(`/test/${test.linkId}/report`, '_blank', 'noopener,noreferrer')}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            View Report
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassageResultsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sequence Results Dialog */}
      <Dialog open={sequenceResultsOpen} onClose={() => setSequenceResultsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Sequence Runs for: {sequenceResultsTitle}
        </DialogTitle>
        <DialogContent>
          {sequenceResultsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : sequenceRuns.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No runs found for this sequence template.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Completed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sequenceRuns.map(run => (
                    <TableRow key={run._id} hover>
                      <TableCell>{run.childName || '—'}</TableCell>
                      <TableCell>{run.teacherId?.name || 'Unknown'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{run.status}</TableCell>
                      <TableCell>{run.completedCount}/{run.totalPassages}</TableCell>
                      <TableCell>{new Date(run.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button 
                            size="small" 
                            variant="contained"
                            onClick={() => window.open(`/sequence/${run.linkId}/report`, '_blank', 'noopener,noreferrer')}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            View Report
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSequenceResultsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Passage Dialog - Matching Dashboard exactly */}
      <Dialog open={createPassageOpen} onClose={() => setCreatePassageOpen(false)} maxWidth="lg" fullWidth PaperProps={{
        sx: { borderRadius: 4, overflow: 'hidden', background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)', minHeight: '80vh' }
      }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 32, mt: 2, mb: 1, letterSpacing: 1, color: '#81c784', background: 'transparent' }}>
          {isEditingPassage ? 'Edit Passage' : 'Create New Passage'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 4, py: 3, minHeight: '600px' }}>
          {/* Left Panel - Form Fields */}
          <Box sx={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Passage Name"
              type="text"
              variant="outlined"
              fullWidth
              value={newPassageName}
              onChange={e => setNewPassageName(e.target.value)}
              sx={{ background: '#fff', borderRadius: 2, input: { fontSize: 18, fontWeight: 600 } }}
            />

            <FormControl sx={{ width: '100%' }}>
              <FormLabel sx={{ fontWeight: 700, color: '#81c784', mb: 2, fontSize: 16 }}>Passage Type</FormLabel>
              <RadioGroup
                value={newPassageType}
                onChange={(e) => setNewPassageType(e.target.value)}
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="assessment" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize={16}>📋 Assessment</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={13}>
                        Text is broken down line-by-line. Students read one line at a time with Next button.
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2.5,
                    m: 0,
                    bgcolor: newPassageType === 'assessment' ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                    borderColor: newPassageType === 'assessment' ? '#2196f3' : '#e0e0e0'
                  }}
                />
                <FormControlLabel 
                  value="story" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize={16}>📚 Story</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={13}>
                        Text is broken down paragraph-by-paragraph. Students read one paragraph at a time.
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2.5,
                    m: 0,
                    bgcolor: newPassageType === 'story' ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                    borderColor: newPassageType === 'story' ? '#9c27b0' : '#e0e0e0'
                  }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                Grade Level *
              </Typography>
              <Select
                value={newPassageGradeLevel}
                onChange={(e) => setNewPassageGradeLevel(e.target.value)}
                displayEmpty
                sx={{ background: '#fff', borderRadius: 2 }}
              >
                <MenuItem value="" disabled>Select Grade Level</MenuItem>
                <MenuItem value="K">Kindergarten</MenuItem>
                <MenuItem value="1">1st Grade</MenuItem>
                <MenuItem value="2">2nd Grade</MenuItem>
                <MenuItem value="3">3rd Grade</MenuItem>
                <MenuItem value="4">4th Grade</MenuItem>
                <MenuItem value="5">5th Grade</MenuItem>
                <MenuItem value="6">6th Grade</MenuItem>
                <MenuItem value="7">7th Grade</MenuItem>
                <MenuItem value="8">8th Grade</MenuItem>
                <MenuItem value="9">9th Grade</MenuItem>
                <MenuItem value="10">10th Grade</MenuItem>
                <MenuItem value="11">11th Grade</MenuItem>
                <MenuItem value="12">12th Grade</MenuItem>
              </Select>
            </FormControl>

            {newPassageType === 'story' && (
              <FormControl fullWidth>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                  Type of Story *
                </Typography>
                <Select
                  value={newPassageStoryType}
                  onChange={(e) => setNewPassageStoryType(e.target.value)}
                  displayEmpty
                  sx={{ background: '#fff', borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Story Type</MenuItem>
                  <MenuItem value="Controlled Decodable">Controlled Decodable</MenuItem>
                  <MenuItem value="Expository">Expository</MenuItem>
                  <MenuItem value="Fiction">Fiction</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                Source *
              </Typography>
              <Select
                value={newPassageSource}
                onChange={(e) => setNewPassageSource(e.target.value)}
                displayEmpty
                sx={{ background: '#fff', borderRadius: 2 }}
              >
                <MenuItem value="" disabled>Select Source</MenuItem>
                <MenuItem value="RGR Blast">RGR Blast</MenuItem>
                <MenuItem value="RGR Countdown">RGR Countdown</MenuItem>
                <MenuItem value="RGR HD Word">RGR HD Word</MenuItem>
                <MenuItem value="RGR Boost">RGR Boost</MenuItem>
                <MenuItem value="DIBELS">DIBELS</MenuItem>
                <MenuItem value="RGR Surveys">RGR Surveys</MenuItem>
                <MenuItem value="ReadWorks">ReadWorks</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <Box
              onDrop={handleImageDrop}
              onDragOver={e => e.preventDefault()}
              sx={{
                width: '100%',
                height: 200,
                border: '2px dashed #81c784',
                borderRadius: 3,
                background: '#f9fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                cursor: 'pointer',
                position: 'relative',
                transition: 'border 0.2s',
                '&:hover': { border: '2px solid #4fc3f7' }
              }}
              onClick={() => document.getElementById('passage-image-input').click()}
            >
              {newPassageImagePreview ? (
                <>
                  <img src={newPassageImagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  <Button size="small" color="error" onClick={e => { e.stopPropagation(); handleImageRemove(); }} sx={{ position: 'absolute', top: 8, right: 8, borderRadius: 2, fontWeight: 700 }}>Remove</Button>
                </>
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 48, color: '#81c784', mb: 1 }} />
                  <Typography color="#81c784" fontWeight={700} fontSize={18}>Upload Cover Image</Typography>
                  <Typography color="#aaa" fontSize={14}>(Optional - Drag & drop or click)</Typography>
                </>
              )}
              <input
                id="passage-image-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </Box>
          </Box>

          {/* Right Panel - Text Editor with Preview */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h6" fontWeight={700} color="#2196f3">
                Passage Content
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {newPassageType && (
                  <Chip 
                    label={newPassageType === 'story' ? 'Will break by paragraphs' : 'Will break by lines'}
                    color={newPassageType === 'story' ? 'secondary' : 'primary'}
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                {newPassageText && (
                  <Chip 
                    label={`${calculateWordCount(newPassageText)} words`}
                    color="default"
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                {isEditingPassage && editingPassageId ? (
                  // Show current lexile level when editing
                  passages.find(p => p._id === editingPassageId)?.lexileLevel ? (
                    <Chip 
                      label={`Current Lexile: ${passages.find(p => p._id === editingPassageId)?.lexileLevel}L`}
                      color="success"
                      size="small"
                      sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                    />
                  ) : (
                    <Chip 
                      label="Lexile: Not calculated"
                      color="warning"
                      size="small"
                      sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                    />
                  )
                ) : (
                  // Show generic message when creating
                  <Chip 
                    label="Lexile calculated on creation"
                    color="info"
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <TextField
                label={`Enter your ${newPassageType === 'story' ? 'story' : 'assessment'} text here`}
                multiline
                fullWidth
                minRows={20}
                value={newPassageText}
                onChange={e => {
                  const text = e.target.value;
                  setNewPassageText(text);
                  console.log('📝 New passage text changed, will calculate on create');
                }}
                sx={{ 
                  flex: 1,
                  background: '#fff', 
                  borderRadius: 2,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 16,
                    lineHeight: 1.6,
                    fontFamily: 'Georgia, serif',
                    height: '100% !important',
                    overflow: 'auto !important'
                  }
                }}
              />
              
              {/* Live Preview of how text will be broken down */}
              {newPassageText && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#666" mb={1}>
                    📋 Preview: How students will see this {newPassageType === 'story' ? 'story' : 'assessment'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {newPassageType === 'story'
                      ? newPassageText.split('\n\n').filter(p => p.trim()).map((paragraph, index) => (
                          <Box key={index} sx={{
                            p: 1.5,
                            background: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            borderLeft: '4px solid #9c27b0'
                          }}>
                            <Typography variant="caption" color="#9c27b0" fontWeight={600}>
                              Paragraph {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                              {paragraph.trim()}
                            </Typography>
                          </Box>
                        ))
                      : newPassageText.split('\n').filter(line => line.trim()).map((line, index) => (
                          <Box key={index} sx={{
                            p: 1,
                            background: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            borderLeft: '4px solid #2196f3'
                          }}>
                            <Typography variant="caption" color="#2196f3" fontWeight={600}>
                              Line {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                              {line.trim()}
                            </Typography>
                          </Box>
                        ))
                    }
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {newPassageType === 'story'
                      ? '💡 Tip: Separate paragraphs with double line breaks for best results'
                      : '💡 Tip: Each line break creates a new reading segment'
                    }
                  </Typography>
                </Box>
              )}

              {/* Hero Video Manager - plays before reading starts - Only show when editing */}
              {isEditingPassage && editingPassageId && (
                <HeroVideoManager
                  passageId={editingPassageId}
                  heroVideo={heroVideo}
                  onHeroVideoChange={setHeroVideo}
                  disabled={uploadingPassage}
                />
              )}

              {/* Line Video Instructions Manager - Only show when editing an existing passage */}
              {isEditingPassage && editingPassageId && newPassageText && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(156, 39, 176, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                  maxHeight: '350px',
                  overflow: 'auto'
                }}>
                  <LineVideoManager
                    passageId={editingPassageId}
                    passageText={newPassageText}
                    passageType={newPassageType}
                    lineVideos={lineVideos}
                    onVideosChange={setLineVideos}
                    disabled={uploadingPassage}
                  />
                </Box>
              )}

              {/* Message for new passages about video uploads */}
              {!isEditingPassage && newPassageText && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(156, 39, 176, 0.05)',
                  borderRadius: 2,
                  border: '1px dashed rgba(156, 39, 176, 0.3)',
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" color="#9c27b0" fontWeight={500}>
                    Video instructions can be added after creating the passage.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create the passage first, then edit it to add videos before each {newPassageType === 'story' ? 'paragraph' : 'line'}.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, borderTop: '1px solid rgba(0,0,0,0.1)', pt: 2 }}>
          <Button onClick={() => setCreatePassageOpen(false)} color="secondary" variant="outlined" sx={{ fontWeight: 'bold', borderRadius: 2, px: 6, py: 1.5, fontSize: 16 }}>Cancel</Button>
          <Button onClick={handleCreatePassage} variant="contained" color="primary" disabled={!newPassageName.trim() || !newPassageText.trim() || !newPassageGradeLevel || !newPassageSource || (newPassageType === 'story' && !newPassageStoryType) || uploadingPassage} sx={{ fontWeight: 'bold', borderRadius: 2, px: 6, py: 1.5, fontSize: 16, background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)' }}>
            {uploadingPassage 
              ? (isEditingPassage ? '⏳ Updating...' : '⏳ Creating...') 
              : (isEditingPassage ? '✨ Update Passage' : '✨ Create Passage')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Sequence Dialog - Matching Dashboard exactly */}
      <Dialog 
        open={sequenceDialogOpen} 
        onClose={() => setSequenceDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth 
        PaperProps={{
          sx: { 
            borderRadius: 4, 
            overflow: 'hidden', 
            background: 'linear-gradient(135deg, #fff8e1 0%, #f3e5ab 100%)',
            minHeight: '70vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 26, mt: 2, mb: 1, letterSpacing: 1, color: '#f57c00', background: 'transparent' }}>
          {isEditingSequence ? 'Edit Passage Sequence' : 'Create Passage Sequence'}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Stepper activeStep={sequenceStep} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Select Passages</StepLabel>
            </Step>
            <Step>
              <StepLabel>Save Template</StepLabel>
            </Step>
          </Stepper>

          {sequenceStep === 0 && (
            <Box>
              <Typography variant="h6" mb={4} textAlign="center" color="text.secondary">
                Select the passages for your sequence. They will be attempted in the order shown.
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  justifyContent: 'center',
                  alignItems: 'stretch'
                }}
              >
                {passages.map((passage, index) => {
                  const sequenceNumber = getPassageSequenceNumber(passage._id);
                  const isSelected = sequenceNumber !== null;
                  return (
                    <Card 
                      key={passage._id}
                      sx={{ 
                        width: 280,
                        maxWidth: '100%',
                        borderRadius: 3, 
                        cursor: 'pointer',
                        border: isSelected ? '3px solid #f57c00' : '2px solid rgba(0,0,0,0.08)',
                        position: 'relative',
                        transform: isSelected ? 'scale(0.98)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                        boxShadow: isSelected ? '0 8px 24px rgba(245,124,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { 
                          transform: isSelected ? 'scale(0.99)' : 'scale(1.02)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                        }
                      }}
                      onClick={() => handlePassageToggle(passage)}
                    >
                      {isSelected && (
                        <Chip
                          label={sequenceNumber}
                          color="warning"
                          sx={{ 
                            position: 'absolute', 
                            top: 12, 
                            right: 12, 
                            zIndex: 2, 
                            fontWeight: 'bold',
                            fontSize: 16,
                            minWidth: 36,
                            height: 36,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}
                        />
                      )}
                      <CardMedia
                        component="img"
                        height="140"
                        image={getPassageImage(passage)}
                        alt={passage.title}
                        sx={{ objectFit: 'cover', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
                      />
                      <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Typography variant="subtitle1" fontWeight={700} fontSize={16} lineHeight={1.3}>
                              {passage.title}
                            </Typography>
                            <Chip 
                              label={passage.passageType === 'story' ? 'Story' : 'Assessment'}
                              size="small"
                              color={passage.passageType === 'story' ? 'secondary' : 'primary'}
                              sx={{ fontSize: '0.6rem', fontWeight: 600, height: '18px' }}
                            />
                          </Box>
                          <Typography color="text.secondary" fontSize={13} lineHeight={1.4} mb={1}>
                            {getExcerpt(passage.text)}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {passage.gradeLevel && (
                            <Chip 
                              label={`Gr.${passage.gradeLevel}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '18px' }}
                            />
                          )}
                          {passage.wordCount && (
                            <Chip 
                              label={`${passage.wordCount}w`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '18px' }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
              {selectedPassages.length > 0 && (
                <Box mt={4} textAlign="center" p={2} borderRadius={2} sx={{ bgcolor: 'rgba(76,175,80,0.1)' }}>
                  <Typography variant="body1" color="success.main" fontWeight={700} fontSize={18}>
                    ✓ {selectedPassages.length} passage{selectedPassages.length !== 1 ? 's' : ''} selected for sequence
                  </Typography>
                </Box>
              )}

              {passages.length === 0 && (
                <Box textAlign="center" py={8}>
                  <PassagesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1}>
                    No Passages Available
                  </Typography>
                  <Typography color="text.secondary">
                    Create some passages first before creating sequences.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {sequenceStep === 1 && (
            <Box display="flex" flexDirection="column" alignItems="center" sx={{ maxWidth: 600, mx: 'auto' }}>
              <Typography variant="h6" mb={2} textAlign="center" color="text.secondary" fontSize={18}>
                Save this sequence template for reuse
              </Typography>
              
              <Box mb={4} width="100%" sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 3, p: 3, border: '1px solid rgba(245,124,0,0.2)' }}>
                <Typography variant="h6" fontWeight={700} mb={3} color="#f57c00" textAlign="center">
                  📚 Selected Sequence ({selectedPassages.length} passages)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedPassages.map((passage, index) => (
                    <Box 
                      key={passage._id} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.9)', 
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Chip 
                        label={index + 1} 
                        color="warning" 
                        sx={{ mr: 3, fontWeight: 'bold', fontSize: 16, minWidth: 40, height: 32 }}
                      />
                      <Box flexGrow={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography fontWeight={700} fontSize={16} color="text.primary">
                            {passage.title}
                          </Typography>
                          <Chip 
                            label={passage.passageType === 'story' ? 'Story' : 'Assessment'}
                            size="small"
                            color={passage.passageType === 'story' ? 'secondary' : 'primary'}
                            sx={{ fontSize: '0.6rem', fontWeight: 600, height: '18px' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" fontSize={13} mb={0.5}>
                          {getExcerpt(passage.text)}
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {passage.gradeLevel && (
                            <Chip 
                              label={`Grade ${passage.gradeLevel}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '16px' }}
                            />
                          )}
                          {passage.wordCount && (
                            <Chip 
                              label={`${passage.wordCount} words`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '16px' }}
                            />
                          )}
                          {passage.lexileLevel && (
                            <Chip 
                              label={`Lexile: ${passage.lexileLevel}`}
                              size="small"
                              variant="outlined"
                              color="success"
                              sx={{ fontSize: '0.55rem', height: '16px' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Save Sequence Mode - Title and Description fields */}
              <Box display="flex" flexDirection="column" alignItems="center" gap={3} width="100%">
                <TextField
                  autoFocus
                  label="Sequence Title *"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={sequenceTitle}
                  onChange={e => setSequenceTitle(e.target.value)}
                  sx={{
                    maxWidth: 450,
                    background: '#fff',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(33,150,243,0.1)',
                    '& .MuiOutlinedInput-root': {
                      fontSize: 18,
                      fontWeight: 600,
                      letterSpacing: 1,
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(33,150,243,0.2)'
                      }
                    },
                  }}
                  inputProps={{ style: { textAlign: 'center' } }}
                  placeholder="e.g., 3rd Grade Reading Assessment Bundle"
                />
                
                <TextField
                  label="Description (Optional)"
                  type="text"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  value={sequenceDescription}
                  onChange={e => setSequenceDescription(e.target.value)}
                  sx={{
                    maxWidth: 450,
                    background: '#fff',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(33,150,243,0.1)',
                    '& .MuiOutlinedInput-root': {
                      fontSize: 16,
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(33,150,243,0.2)'
                      }
                    },
                  }}
                  placeholder="Add a description to help you remember what this sequence is for..."
                />
                
                <Box sx={{ maxWidth: 450, width: '100%', textAlign: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary', 
                      fontSize: 15,
                      fontStyle: 'italic',
                      backgroundColor: 'rgba(33, 150, 243, 0.08)',
                      padding: '12px 20px',
                      borderRadius: 3,
                      border: '1px solid rgba(33, 150, 243, 0.2)',
                      fontWeight: 500
                    }}
                  >
                    💾 {isEditingSequence ? 'Update this sequence template' : 'Save this sequence as a global template for all teachers to use'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', pb: 4, px: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button 
            onClick={() => setSequenceDialogOpen(false)} 
            color="secondary" 
            variant="outlined"
            sx={{ 
              fontWeight: 700, 
              borderRadius: 3, 
              px: 5, 
              py: 2, 
              fontSize: 16,
              border: '2px solid rgba(0,0,0,0.2)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
            }}
          >
            Cancel
          </Button>
          <Box display="flex" gap={2}>
            {sequenceStep === 1 && (
              <Button 
                onClick={handleSequenceBack} 
                color="secondary" 
                variant="outlined"
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 3, 
                  px: 5, 
                  py: 2, 
                  fontSize: 16,
                  border: '2px solid #f57c00',
                  color: '#f57c00',
                  '&:hover': { bgcolor: 'rgba(245,124,0,0.1)' }
                }}
              >
                ← Back
              </Button>
            )}
            {sequenceStep === 0 ? (
              <Button 
                onClick={handleSequenceNext} 
                variant="contained" 
                disabled={selectedPassages.length === 0} 
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 3, 
                  px: 6, 
                  py: 2, 
                  fontSize: 16,
                  background: selectedPassages.length === 0 ? 'rgba(0,0,0,0.12)' : 'linear-gradient(90deg, #f57c00 0%, #ff9800 100%)',
                  boxShadow: selectedPassages.length === 0 ? 'none' : '0 4px 12px rgba(245,124,0,0.3)',
                  '&:hover': { 
                    background: selectedPassages.length === 0 ? 'rgba(0,0,0,0.12)' : 'linear-gradient(90deg, #e65100 0%, #f57c00 100%)'
                  }
                }}
              >
                Next Step →
              </Button>
            ) : (
              <Button 
                onClick={handleSaveSequence} 
                variant="contained" 
                disabled={!sequenceTitle.trim() || savingSequence}
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 3, 
                  px: 6, 
                  py: 2, 
                  fontSize: 16,
                  background: (!sequenceTitle.trim() || savingSequence)
                    ? 'rgba(0,0,0,0.12)' 
                    : 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)',
                  boxShadow: (!sequenceTitle.trim() || savingSequence)
                    ? 'none' 
                    : '0 4px 12px rgba(33,150,243,0.3)',
                  '&:hover': { 
                    background: (!sequenceTitle.trim() || savingSequence)
                      ? 'rgba(0,0,0,0.12)' 
                      : 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)'
                  }
                }}
              >
                {savingSequence 
                  ? (isEditingSequence ? '⏳ Updating...' : '⏳ Saving...') 
                  : (isEditingSequence ? '💾 Update Sequence' : '💾 Save Sequence')
                }
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Settings Panel - Full implementation from Settings.js
function SettingsPanel({ settings, setSettings, settingsConfig, activeCategory, setActiveCategory, unsavedChanges, setUnsavedChanges, saving, setSaving, showSnackbar, teacher, loading }) {
  const navigate = useNavigate();
  const [activePromptVersion, setActivePromptVersion] = React.useState(null);

  React.useEffect(() => {
    fetchActivePromptVersion();
  }, []);

  const fetchActivePromptVersion = async () => {
    try {
      const response = await api.get('/api/prompt-versions/active');
      setActivePromptVersion(response.data);
    } catch (error) {
      console.log('No active prompt version found or error:', error);
      setActivePromptVersion(null);
    }
  };

  const handleSettingChange = (key, value) => {
    setUnsavedChanges(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getCurrentValue = (key) => {
    return unsavedChanges.hasOwnProperty(key) 
      ? unsavedChanges[key] 
      : settings[key]?.value || '';
  };

  const saveSetting = async (key) => {
    if (!unsavedChanges.hasOwnProperty(key)) return;

    try {
      setSaving(true);
      const value = unsavedChanges[key];
      
      await api.put(`/api/settings/${key}`, {
        value: value,
        description: getSettingConfig(key)?.description,
        category: getCategoryByKey(key)
      });

      // Update local settings
      setSettings(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          value: value,
          updatedBy: teacher,
          updatedAt: new Date().toISOString()
        }
      }));

      // Remove from unsaved changes
      setUnsavedChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[key];
        return newChanges;
      });

      showSnackbar('Setting saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving setting:', error);
      showSnackbar('Failed to save setting', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetSetting = (key) => {
    setUnsavedChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[key];
      return newChanges;
    });
  };

  const getSettingConfig = (key) => {
    for (const category of Object.values(settingsConfig)) {
      const setting = category.settings.find(s => s.key === key);
      if (setting) return setting;
    }
    return null;
  };

  const getCategoryByKey = (key) => {
    for (const [categoryKey, category] of Object.entries(settingsConfig)) {
      if (category.settings.some(s => s.key === key)) {
        return categoryKey;
      }
    }
    return 'general';
  };

  const hasUnsavedChanges = (key) => {
    return unsavedChanges.hasOwnProperty(key);
  };

  const renderSettingField = (settingConfig) => {
    const { key, label, type, rows, options, required } = settingConfig;
    const value = getCurrentValue(key);
    const hasChanges = hasUnsavedChanges(key);

    // Special handling for GPT analysis instruction - link to version management
    if (key === 'gpt_analysis_instruction') {
      return (
        <Card variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Prompt Version Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                GPT analysis prompts are managed through a comprehensive version control system.
              </Typography>
            </Box>
            <Chip
              label={activePromptVersion ? `Active: v${activePromptVersion.version}` : 'No Active Version'}
              color="success"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {activePromptVersion && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* Version Info */}
              <Stack spacing={1} sx={{ mb: 2 }}>
                {activePromptVersion.label && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Label:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {activePromptVersion.label}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Created:</Typography>
                  <Typography variant="body2">
                    {new Date(activePromptVersion.createdAt).toLocaleString()}
                    {activePromptVersion.createdBy && ` by ${activePromptVersion.createdBy.name}`}
                  </Typography>
                </Box>
                {activePromptVersion.activatedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Activated:</Typography>
                    <Typography variant="body2">
                      {new Date(activePromptVersion.activatedAt).toLocaleString()}
                      {activePromptVersion.activatedBy && ` by ${activePromptVersion.activatedBy.name}`}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Character count:</Typography>
                  <Typography variant="body2">{activePromptVersion.content.length} characters</Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Full Prompt Content */}
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Current Prompt Content:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 400, overflow: 'auto', bgcolor: '#f5f5f5' }}>
                <Typography component="pre" sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  {activePromptVersion.content}
                </Typography>
              </Paper>
            </>
          )}

          <Button
            variant="contained"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/prompt-versions')}
            sx={{ fontWeight: 600 }}
            fullWidth
          >
            Manage Prompt Versions
          </Button>
        </Card>
      );
    }

    if (type === 'textarea') {
      return (
        <TextField
          fullWidth
          multiline
          rows={rows || 4}
          label={label}
          value={value}
          onChange={(e) => handleSettingChange(key, e.target.value)}
          required={required}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              lineHeight: 1.4,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            }
          }}
        />
      );
    }

    if (type === 'select') {
      return (
        <TextField
          fullWidth
          select
          label={label}
          value={value}
          onChange={(e) => handleSettingChange(key, e.target.value)}
          required={required}
          variant="outlined"
          SelectProps={{
            native: true
          }}
        >
          <option value="">Select...</option>
          {options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => handleSettingChange(key, e.target.value)}
        required={required}
        variant="outlined"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const activeConfig = settingsConfig[activeCategory];

  return (
    <Box display="flex" gap={4}>
      {/* Sidebar Navigation */}
      <Paper sx={{ 
        width: 280, 
        height: 'fit-content',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <Box sx={{ p: 3, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={700} color="#333">
            Categories
          </Typography>
        </Box>
        <List sx={{ p: 0 }}>
          {Object.entries(settingsConfig).map(([categoryKey, category]) => (
            <ListItem key={categoryKey} disablePadding>
              <ListItemButton
                selected={activeCategory === categoryKey}
                onClick={() => setActiveCategory(categoryKey)}
                sx={{
                  py: 2,
                  px: 3,
                  '&.Mui-selected': {
                    backgroundColor: `${category.color}15`,
                    borderRight: `3px solid ${category.color}`,
                    '& .MuiListItemIcon-root': {
                      color: category.color
                    },
                    '& .MuiListItemText-primary': {
                      color: category.color,
                      fontWeight: 700
                    }
                  },
                  '&:hover': {
                    backgroundColor: `${category.color}08`
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {category.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={category.title}
                  secondary={category.description}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '15px'
                  }}
                  secondaryTypographyProps={{
                    fontSize: '12px'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Content Area */}
      <Box flex={1}>
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          {/* Content Header */}
          <Box sx={{ 
            p: 4, 
            background: `linear-gradient(135deg, ${activeConfig.color}15 0%, ${activeConfig.color}05 100%)`,
            borderBottom: '1px solid #e0e0e0'
          }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar sx={{ bgcolor: activeConfig.color, width: 56, height: 56 }}>
                {activeConfig.icon}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700} color="#333">
                  {activeConfig.title} Settings
                </Typography>
                <Typography color="text.secondary" fontSize={16}>
                  {activeConfig.description}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Settings Content */}
          <Box sx={{ p: 4 }}>
            {activeConfig.settings.map((settingConfig, index) => {
              const { key, description } = settingConfig;
              const hasChanges = hasUnsavedChanges(key);
              const settingData = settings[key];
              
              return (
                <Card 
                  key={key}
                  sx={{ 
                    mb: index < activeConfig.settings.length - 1 ? 4 : 0,
                    borderRadius: 2,
                    border: hasChanges ? `2px solid ${activeConfig.color}` : '1px solid #e0e0e0',
                    boxShadow: hasChanges ? `0 4px 12px ${activeConfig.color}20` : '0 2px 8px rgba(0,0,0,0.04)'
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {settingConfig.label}
                          </Typography>
                          {hasChanges && (
                            <Chip 
                              label="Unsaved Changes" 
                              size="small" 
                              color="warning"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {description}
                        </Typography>
                      </Box>
                      
                      <Tooltip title="Setting Information">
                        <IconButton size="small">
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {renderSettingField(settingConfig)}

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                      <Box>
                        {settingData?.updatedBy && (
                          <Typography variant="caption" color="text.secondary">
                            Last updated by <strong>{settingData.updatedBy.name}</strong> on{' '}
                            {new Date(settingData.updatedAt).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                      
                      <Stack direction="row" spacing={2}>
                        {hasChanges && (
                          <Button
                            variant="outlined"
                            startIcon={<UndoIcon />}
                            onClick={() => resetSetting(key)}
                            sx={{ borderRadius: 2, fontWeight: 600 }}
                          >
                            Reset
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => saveSetting(key)}
                          disabled={!hasChanges || saving}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            minWidth: 100,
                            background: hasChanges 
                              ? `linear-gradient(90deg, ${activeConfig.color} 0%, ${activeConfig.color}dd 100%)`
                              : undefined
                          }}
                        >
                          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function TeachersPanel({ teachers, loading, showSnackbar, fetchTeachers }) {
  const [teacherDialog, setTeacherDialog] = useState({ open: false, mode: 'create', teacher: null });
  const [teacherForm, setTeacherForm] = useState({ name: '', email: '', password: '', role: 'teacher' });
  const [submitting, setSubmitting] = useState(false);

  const handleCreateTeacher = () => {
    setTeacherForm({ name: '', email: '', password: '', role: 'teacher' });
    setTeacherDialog({ open: true, mode: 'create', teacher: null });
  };

  const handleEditTeacher = (teacher) => {
    setTeacherForm({ 
      name: teacher.name, 
      email: teacher.email, 
      password: '', 
      role: teacher.role || 'teacher' 
    });
    setTeacherDialog({ open: true, mode: 'edit', teacher });
  };

  const handleDeleteTeacher = async (teacher) => {
    if (window.confirm(`Are you sure you want to delete ${teacher.name}?`)) {
      try {
        await api.delete(`/api/admin/teachers/${teacher._id}`);
        showSnackbar('Teacher deleted successfully', 'success');
        fetchTeachers();
      } catch (error) {
        showSnackbar(error.response?.data?.error || 'Failed to delete teacher', 'error');
      }
    }
  };

  const handleSubmitTeacher = async () => {
    if (!teacherForm.name || !teacherForm.email) {
      showSnackbar('Name and email are required', 'error');
      return;
    }

    if (teacherDialog.mode === 'create' && !teacherForm.password) {
      showSnackbar('Password is required for new teachers', 'error');
      return;
    }

    try {
      setSubmitting(true);
      
      if (teacherDialog.mode === 'create') {
        await api.post('/api/admin/teachers', teacherForm);
        showSnackbar('Teacher created successfully', 'success');
      } else {
        const updateData = {
          name: teacherForm.name,
          email: teacherForm.email,
          role: teacherForm.role
        };
        await api.put(`/api/admin/teachers/${teacherDialog.teacher._id}`, updateData);
        showSnackbar('Teacher updated successfully', 'success');
      }
      
      setTeacherDialog({ open: false, mode: 'create', teacher: null });
      fetchTeachers();
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Failed to save teacher', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Teacher Management ({teachers.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTeacher}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Add Teacher
        </Button>
      </Box>

      {/* Teachers Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teachers.map((teacher) => (
                <TableRow key={teacher._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ 
                        bgcolor: teacher.role === 'admin' ? '#ff7043' : '#4fc3f7',
                        width: 32,
                        height: 32
                      }}>
                        {teacher.name[0]?.toUpperCase()}
                      </Avatar>
                      <Typography fontWeight={600}>{teacher.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{teacher.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={teacher.role || 'teacher'} 
                      color={teacher.role === 'admin' ? 'error' : 'primary'}
                      size="small"
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(teacher.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditTeacher(teacher)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteTeacher(teacher)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Teacher Dialog */}
      <Dialog 
        open={teacherDialog.open} 
        onClose={() => setTeacherDialog({ open: false, mode: 'create', teacher: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {teacherDialog.mode === 'create' ? 'Add New Teacher' : 'Edit Teacher'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={teacherForm.name}
              onChange={(e) => setTeacherForm(prev => ({ ...prev, name: e.target.value }))}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={teacherForm.email}
              onChange={(e) => setTeacherForm(prev => ({ ...prev, email: e.target.value }))}
              variant="outlined"
            />
            {teacherDialog.mode === 'create' && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={teacherForm.password}
                onChange={(e) => setTeacherForm(prev => ({ ...prev, password: e.target.value }))}
                variant="outlined"
              />
            )}
            <TextField
              fullWidth
              select
              label="Role"
              value={teacherForm.role}
              onChange={(e) => setTeacherForm(prev => ({ ...prev, role: e.target.value }))}
              variant="outlined"
              SelectProps={{ native: true }}
            >
              <option value="teacher">Teacher</option>
              <option value="admin">Administrator</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setTeacherDialog({ open: false, mode: 'create', teacher: null })}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitTeacher}
            disabled={submitting}
            sx={{ borderRadius: 2, fontWeight: 600, minWidth: 100 }}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : (
              teacherDialog.mode === 'create' ? 'Create' : 'Update'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PassagesPanel({ 
  passages, 
  allPassages,
  loading,
  teachers,
  passageSearchQuery,
  setPassageSearchQuery,
  filterGradeLevels,
  setFilterGradeLevels,
  filterPassageType,
  setFilterPassageType,
  filterStoryTypes,
  setFilterStoryTypes,
  filterSources,
  setFilterSources,
  filterLexileBucket,
  setFilterLexileBucket,
  filterPassageTeacher,
  setFilterPassageTeacher,
  filtersExpanded,
  setFiltersExpanded,
  activeFilterCount,
  getFilterChips,
  resetPassageFilters,
  onCreatePassage,
  onEditPassage,
  onDeletePassage,
  onViewPassageResults
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and Filter Header */}
      <Box sx={{ mb: 3 }}>
        {/* Search and Filter Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search passages..."
            value={passageSearchQuery}
            onChange={(e) => setPassageSearchQuery(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 320 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                fontSize: 14,
                height: 40,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  boxShadow: '0 0 0 2px rgba(79, 195, 247, 0.2)',
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#4fc3f7', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
          />

          <Button
            variant={filtersExpanded ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              height: 40,
              minWidth: 140,
              ...(filtersExpanded ? {
                background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                boxShadow: '0 2px 8px rgba(79,195,247,0.3)',
              } : {
                borderColor: '#4fc3f7',
                color: '#4fc3f7',
                '&:hover': {
                  borderColor: '#29b6f6',
                  backgroundColor: 'rgba(79, 195, 247, 0.04)',
                }
              })
            }}
          >
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
        </Box>

        {/* Active Filter Chips */}
        {activeFilterCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {getFilterChips().map(chip => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={chip.onDelete}
                variant="filled"
                color="primary"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 500,
                  fontSize: 12,
                  height: 28,
                  '& .MuiChip-deleteIcon': {
                    fontSize: 16,
                    color: 'rgba(255,255,255,0.8)',
                    '&:hover': {
                      color: 'rgba(255,255,255,1)',
                    }
                  }
                }}
              />
            ))}
            <Button
              variant="text"
              color="secondary"
              size="small"
              onClick={resetPassageFilters}
              sx={{ 
                fontWeight: 600,
                fontSize: 12,
                minWidth: 'auto',
                px: 1,
                textDecoration: 'underline',
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.04)',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        )}

        {/* Expandable Filter Panel */}
        <Fade in={filtersExpanded}>
          <Paper 
            elevation={3} 
            sx={{ 
              mt: 2, 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
              border: '1px solid rgba(79, 195, 247, 0.2)',
              display: filtersExpanded ? 'block' : 'none'
            }}
          >
            <Typography variant="h6" fontWeight={600} color="text.primary" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon color="primary" />
              Filter Passages
            </Typography>
            
            <Grid container spacing={3}>
              {/* Passage Type */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                    📝 Passage Type
                  </Typography>
                  <Select
                    value={filterPassageType}
                    onChange={(e) => setFilterPassageType(e.target.value)}
                    displayEmpty
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(79, 195, 247, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(79, 195, 247, 0.5)',
                      }
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="assessment">📋 Assessment</MenuItem>
                    <MenuItem value="story">📚 Story</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Grade Level */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                    🎓 Grade Level
                  </Typography>
                  <Select
                    multiple
                    value={filterGradeLevels}
                    onChange={(e) => setFilterGradeLevels(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    renderValue={(selected) => selected.length === 0 ? 'All Grades' : selected.join(', ')}
                    displayEmpty
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(76, 175, 80, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(76, 175, 80, 0.5)',
                      }
                    }}
                  >
                    {['K','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                      <MenuItem key={g} value={g}>
                        <Checkbox 
                          checked={filterGradeLevels.indexOf(g) > -1} 
                          sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
                        />
                        <MuiListItemText primary={g === 'K' ? 'Kindergarten' : `Grade ${g}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Genre (Story Types) */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                    🎭 Genre
                  </Typography>
                  <Select
                    multiple
                    value={filterStoryTypes}
                    onChange={(e) => setFilterStoryTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    renderValue={(selected) => selected.length === 0 ? 'All Genres' : selected.join(', ')}
                    displayEmpty
                    disabled={filterPassageType === 'assessment'}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(156, 39, 176, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(156, 39, 176, 0.5)',
                      }
                    }}
                  >
                    {['Controlled Decodable','Expository','Fiction'].map(t => (
                      <MenuItem key={t} value={t}>
                        <Checkbox 
                          checked={filterStoryTypes.indexOf(t) > -1}
                          sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                        />
                        <MuiListItemText primary={t} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Lexile Level */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                    📊 Lexile Level
                  </Typography>
                  <Select
                    value={filterLexileBucket}
                    onChange={(e) => setFilterLexileBucket(e.target.value)}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 152, 0, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 152, 0, 0.5)',
                      }
                    }}
                  >
                    <MenuItem value="any">Any Level</MenuItem>
                    <MenuItem value="unknown">❓ Unknown</MenuItem>
                    <MenuItem value="0-199">🟢 0–199L (Beginner)</MenuItem>
                    <MenuItem value="200-499">🔵 200–499L (Elementary)</MenuItem>
                    <MenuItem value="500-799">🟡 500–799L (Middle)</MenuItem>
                    <MenuItem value="800+">🔴 800L+ (Advanced)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Source */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                    📚 Source
                  </Typography>
                  <Select
                    multiple
                    value={filterSources}
                    onChange={(e) => setFilterSources(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    renderValue={(selected) => selected.length === 0 ? 'All Sources' : selected.join(', ')}
                    displayEmpty
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(63, 81, 181, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(63, 81, 181, 0.5)',
                      }
                    }}
                  >
                    {['RGR Blast','RGR Countdown','RGR HD Word','RGR Boost','DIBELS','RGR Surveys','ReadWorks','Other'].map(s => (
                      <MenuItem key={s} value={s}>
                        <Checkbox 
                          checked={filterSources.indexOf(s) > -1}
                          sx={{ color: '#3f51b5', '&.Mui-checked': { color: '#3f51b5' } }}
                        />
                        <MuiListItemText primary={s} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Teacher */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                    👨‍🏫 Teacher
                  </Typography>
                  <Select
                    value={filterPassageTeacher}
                    onChange={(e) => setFilterPassageTeacher(e.target.value)}
                    displayEmpty
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(76, 175, 80, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(76, 175, 80, 0.5)',
                      }
                    }}
                  >
                    <MenuItem value="">All Teachers</MenuItem>
                    {teachers.map(teacher => (
                      <MenuItem key={teacher._id} value={teacher._id}>
                        👨‍🏫 {teacher.name} {teacher.role === 'admin' ? '(Admin)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {passages.length} of {allPassages.length} passages
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={resetPassageFilters}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  Clear All Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setFiltersExpanded(false)}
                  sx={{ 
                    borderRadius: 2, 
                    fontWeight: 600,
                    background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                  }}
                >
                  Apply Filters
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Box>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          All Passages ({passages.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreatePassage}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Create Passage
        </Button>
      </Box>

      {/* Passages Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Words</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Lexile</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {passages.map((passage) => (
                <TableRow key={passage._id} hover>
                  <TableCell>
                    <Typography fontWeight={600} noWrap>
                      {passage.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {passage.text?.substring(0, 60)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={passage.passageType || 'assessment'} 
                      color={passage.passageType === 'story' ? 'secondary' : 'primary'}
                      size="small"
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`Grade ${passage.gradeLevel}`} 
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {passage.wordCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {passage.lexileLevel ? (
                      <Chip 
                        label={`${passage.lexileLevel}L`} 
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Unknown
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {passage.source || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ 
                        bgcolor: passage.createdBy?.role === 'admin' ? '#ff7043' : '#4fc3f7',
                        width: 24,
                        height: 24,
                        fontSize: '12px'
                      }}>
                        {passage.createdBy?.name?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Typography variant="body2">
                        {passage.createdBy?.name || 'Unknown'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(passage.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={passage.isAdminCreated ? 'Visible to All' : `Visible to ${passage.createdBy?.name || 'Unknown'}`} 
                      color={passage.isAdminCreated ? 'success' : 'info'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="View Results">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => onViewPassageResults && onViewPassageResults(passage)}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'rgba(33, 150, 243, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <AnalyticsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => onEditPassage(passage)}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => onDeletePassage(passage)}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {passages.length === 0 && (
        <Box textAlign="center" py={8}>
          <PassagesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" mb={1}>
            {allPassages.length === 0 ? 'No Passages Found' : 'No Passages Match Your Filters'}
          </Typography>
          <Typography color="text.secondary">
            {allPassages.length === 0 ? 'No passages have been created yet.' : 'Try adjusting your filter criteria.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function SequencesPanel({ 
  sequences, 
  allSequences,
  loading,
  teachers,
  sequenceSearchQuery,
  setSequenceSearchQuery,
  filterSequenceTeacher,
  setFilterSequenceTeacher,
  onCreateSequence,
  passages,
  onEditSequence,
  onDeleteSequence,
  onViewSequenceResults
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Header */}
      <Box sx={{ mb: 3 }}>
        {/* Search Input */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search sequences..."
            value={sequenceSearchQuery}
            onChange={(e) => setSequenceSearchQuery(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 400 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                fontSize: 14,
                height: 40,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  boxShadow: '0 0 0 2px rgba(255, 152, 0, 0.2)',
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
          />
          
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={filterSequenceTeacher}
              onChange={(e) => setFilterSequenceTeacher(e.target.value)}
              displayEmpty
              size="small"
              sx={{ 
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                height: 40,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(76, 175, 80, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(76, 175, 80, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(76, 175, 80, 0.7)',
                }
              }}
            >
              <MenuItem value="">All Teachers</MenuItem>
              {teachers.map(teacher => (
                <MenuItem key={teacher._id} value={teacher._id}>
                  👨‍🏫 {teacher.name} {teacher.role === 'admin' ? '(Admin)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(sequenceSearchQuery || filterSequenceTeacher) && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {sequences.length} of {allSequences.length} sequences
            </Typography>
          )}
        </Box>

        {/* Active Filter Chips */}
        {(sequenceSearchQuery || filterSequenceTeacher) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {sequenceSearchQuery && (
              <Chip
                label={`"${sequenceSearchQuery}"`}
                onDelete={() => setSequenceSearchQuery('')}
                variant="filled"
                color="secondary"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 500,
                  fontSize: 12,
                  height: 28,
                  '& .MuiChip-deleteIcon': {
                    fontSize: 16,
                    color: 'rgba(255,255,255,0.8)',
                    '&:hover': {
                      color: 'rgba(255,255,255,1)',
                    }
                  }
                }}
              />
            )}
            {filterSequenceTeacher && (
              <Chip
                label={`Teacher: ${teachers.find(t => t._id === filterSequenceTeacher)?.name || 'Unknown'}`}
                onDelete={() => setFilterSequenceTeacher('')}
                variant="filled"
                color="primary"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 500,
                  fontSize: 12,
                  height: 28,
                  '& .MuiChip-deleteIcon': {
                    fontSize: 16,
                    color: 'rgba(255,255,255,0.8)',
                    '&:hover': {
                      color: 'rgba(255,255,255,1)',
                    }
                  }
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          All Sequences ({sequences.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateSequence}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Create Sequence
        </Button>
      </Box>

      {/* Sequences Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Passages</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Grade Levels</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sequences.map((sequence) => (
                <TableRow key={sequence._id} hover>
                  <TableCell>
                    <Typography fontWeight={600} noWrap>
                      {sequence.title}
                    </Typography>
                    {sequence.description && (
                      <Typography variant="caption" color="text.secondary">
                        {sequence.description.substring(0, 60)}...
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Chip 
                        label={`${sequence.totalPassages} Total`}
                        size="small"
                        variant="outlined"
                      />
                      {sequence.totalStories > 0 && (
                        <Chip 
                          label={`${sequence.totalStories} Stories`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                      {sequence.totalAssessments > 0 && (
                        <Chip 
                          label={`${sequence.totalAssessments} Tests`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {sequence.gradeLevels?.slice(0, 3).map(grade => (
                        <Chip
                          key={grade}
                          label={`Grade ${grade}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      ))}
                      {sequence.gradeLevels?.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{sequence.gradeLevels.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      ~{sequence.estimatedDuration} min
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ 
                        bgcolor: sequence.teacherId?.role === 'admin' ? '#ff7043' : '#4fc3f7',
                        width: 24,
                        height: 24,
                        fontSize: '12px'
                      }}>
                        {sequence.teacherId?.name?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Typography variant="body2">
                        {sequence.teacherId?.name || 'Unknown'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(sequence.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={sequence.isAdminCreated ? 'Visible to All' : `Visible to ${sequence.teacherId?.name || 'Unknown'}`} 
                      color={sequence.isAdminCreated ? 'success' : 'info'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="View Results">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => onViewSequenceResults && onViewSequenceResults(sequence)}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'rgba(33, 150, 243, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <AnalyticsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => onEditSequence(sequence)}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => onDeleteSequence(sequence)}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {sequences.length === 0 && (
        <Box textAlign="center" py={8}>
          <SequencesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" mb={1}>
            {allSequences.length === 0 ? 'No Sequences Found' : 'No Sequences Match Your Search'}
          </Typography>
          <Typography color="text.secondary">
            {allSequences.length === 0 ? 'No saved sequences have been created yet.' : 'Try adjusting your search criteria.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function AnalyticsPanel({ analytics, loading }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const {
    totalTeachers,
    totalPassages,
    totalSequences,
    totalTests,
    recentTests
  } = analytics;

  return (
    <Box>
      {/* Header */}
      <Typography variant="h5" fontWeight={700} mb={3}>
        System Analytics & Reports
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
            color: 'white'
          }}>
            <PeopleIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" fontWeight={700}>
              {totalTeachers}
            </Typography>
            <Typography variant="h6">
              Teachers
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #81c784 0%, #66bb6a 100%)',
            color: 'white'
          }}>
            <PassagesIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" fontWeight={700}>
              {totalPassages}
            </Typography>
            <Typography variant="h6">
              Passages
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
            color: 'white'
          }}>
            <SequencesIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" fontWeight={700}>
              {totalSequences}
            </Typography>
            <Typography variant="h6">
              Sequences
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f06292 0%, #ec407a 100%)',
            color: 'white'
          }}>
            <AnalyticsIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" fontWeight={700}>
              {totalTests}
            </Typography>
            <Typography variant="h6">
              Tests Taken
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={700}>
            Recent Test Activity
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Passage</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTests?.slice(0, 10).map((test) => (
                <TableRow key={test._id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {test.childName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {test.passageId?.title || 'Unknown Passage'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ 
                        bgcolor: test.teacherId?.role === 'admin' ? '#ff7043' : '#4fc3f7',
                        width: 24,
                        height: 24,
                        fontSize: '12px'
                      }}>
                        {test.teacherId?.name?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Typography variant="body2">
                        {test.teacherId?.name || 'Unknown'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(test.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={test.status || 'completed'} 
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {(!recentTests || recentTests.length === 0) && (
          <Box textAlign="center" py={6}>
            <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" mb={1}>
              No Recent Activity
            </Typography>
            <Typography color="text.secondary">
              No test activity has been recorded yet.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}